<template>
    <section class="product-select netto-block">
        <media-only desktop no-wrap displayProp="flex" class="flex-column">
            <h3 v-if="productDetails.soldOut" class="netto-block__title mb-3">
                {{ $t('ProductDetails.productSelect.soldOut') }}
            </h3>
            <h3 v-else-if="noPriceProvided" class="netto-block__title mb-3">
                {{ $t('ProductDetails.productSelect.selectTrip') }}
            </h3>

            <!--Arrival date header-->
            <div v-else-if="arrivalDate" class="arrival-date darker-text">
                <button @click="backToCalendar" class="btn btn-link" title="Zurück">
                    <IconSvg filepath="back.svg"></IconSvg>
                </button>
                <p>
                    {{ $t('ProductDetails.productSelect.arrival') }} <span>{{ arrivalDate }}</span>
                </p>
            </div>

            <h3 v-else class="netto-block__title mb-3">{{ productDetails.title }}</h3>
        </media-only>

        <div class="px-4 text-center" v-if="productDetails.availableUntil">
            <CountdownTimer
                :available-until="productDetails.availableUntil"
                :back-button-visible="showBiddingForm"
                show-heading
                @back="selectedVariant = null"
                @expired="isExpired = true"
                class="product-select__countdown mb-3"
            />
        </div>

        <div class="product-select__body">
            <SoldOutSubscribe v-if="productDetails.soldOut" :product-details="productDetails" class="p-0">
            </SoldOutSubscribe>

            <ProductExpiredSubscribe v-else-if="isExpired" :product-details="productDetails" />

            <NettoProductSelectBidding
                v-else-if="showBiddingForm"
                :selected-variant="selectedVariant"
                class="product-select__bidding"
            />

            <template v-else-if="productDetails.available && productDetails.directBooking && !variants.length">
                <p class="select-title">
                    {{ $t('ProductDetails.productSelect.selectWithArrivalDate') }}
                </p>
                <Calendar :availableDates="arrivalDates" @update:selected="dateSelect"> </Calendar>
            </template>

            <template v-else>
                <!--Displaying back button and selected date on top of variants list on mobile-->
                <media-only mobile>
                    <div v-if="arrivalDate" class="arrival-date">
                        <button @click="backToCalendar" class="btn btn-link" :title="$t('Generic.goback')">
                            <IconSvg filepath="back.svg" />
                        </button>
                        <p>
                            {{ $t('ProductDetails.productSelect.arrival') }} <span>{{ arrivalDate }}</span>
                        </p>
                    </div>
                </media-only>

                <template v-if="!isOtherProduct && !isUpselling">
                    <p v-if="isAccessCodeRequired" v-skeleton="!isEmptyDetails" class="select-title">
                        <template v-if="isDelayPayment"> &nbsp; </template>
                        <template v-else>
                            {{ $t('ProductDetails.productSelect.selectWithCopayment') }}
                        </template>
                    </p>
                    <p v-else v-skeleton="!isEmptyDetails" class="select-title">
                        {{
                            isValueVoucher
                                ? $t('ProductDetails.productSelect.selectVoucher')
                                : $t('ProductDetails.productSelect.selectOffer')
                        }}
                    </p>
                </template>

                <UpsellingOptions
                    v-if="isUpselling"
                    :selected-variant="selectedVariant"
                    :variants="variants"
                    @variantSelected="onUpsellVariantSelection"
                    class="mb-3"
                />

                <template v-else>
                    <NettoVariantItem
                        v-for="(variant, index) in variants"
                        :key="'variant' + index"
                        v-skeleton="!isEmptyDetails"
                        :variant="variant"
                        :arrivalDate="arrivalDate"
                        :isVoucher="isValueVoucher"
                        :isAuctionOffer="isAuctionOffer"
                        class="product-select__netto-variant"
                        :class="{ first: index === 0 }"
                        @variantSelected="gtmTrackVariantSelection"
                    />
                </template>
            </template>
        </div>

        <ProductSelectBottomFooter
            v-if="isUpselling"
            :selected-variant="selectedVariant"
            :checkoutAvailableFrom="productDetails.checkoutAvailableFrom"
            @submit="gtmTrackVariantSelection"
            class="product-select__upselling-footer"
        />
    </section>
</template>

<script>
import ProductSelect from '@/components/product/selection/ProductSelect.vue';
import NettoVariantItem from '@/themes/nettovorteilswelt/components/product/NettoVariantItem';
import CountdownTimer from '@/components/product/countdown/CountdownTimer.vue';
import NettoProductSelectBidding from '@/themes/nettovorteilswelt/components/product/NettoProductSelectBidding.vue';

export default {
    name: 'NettoProductSelect',
    components: { NettoProductSelectBidding, CountdownTimer, NettoVariantItem },
    extends: ProductSelect,

    created() {
        //TODO: think on doing everywhere
        if ((this.productDetails.variants || []).length === 1 && this.isAuctionOffer) {
            this.selectedVariant = this.productDetails.variants[0];
        }
    }
};
</script>

<style scoped lang="scss">
@use '@/styles/variables';
@use '@/styles/bootstrap/breakpoints' as breakpoints;
@use '@/styles/_mixins.scss';

.product-select {
    $padding: 15px;
    padding: $padding;
    padding-top: 30px; //to fit the top absolute discount

    &__body {
        width: 100%;
        max-width: 400px;
        margin: 0 auto;
    }

    &__netto-variant {
        &:not(:last-child) {
            margin-bottom: 20px; // to fit with absolutely positioned discount of the next variant
        }

        &:not(.first) {
            padding-top: 25px;
            border-top: 1px solid variables.$border-color;
        }
    }

    @include mixins.mobile-only {
        &__product-select-footer {
            margin-left: -#{$padding}px;
            margin-right: -#{$padding}px;
            margin-bottom: -#{$padding}px;
        }

        &__bidding {
            ::v-deep {
                .netto-product-bidding__total {
                    color: var(--primary);
                }

                .bid-input {
                    background-color: #fafafa;
                }
            }
        }
    }
}
</style>
